import React, { createContext, useContext, useMemo, useState } from 'react';

import { defaultEndpoint, defaultScriptUrlPattern } from '@fingerprintjs/fingerprintjs-pro';
import { useVisitorData, CacheLocation, FpjsProvider } from '@fingerprintjs/fingerprintjs-pro-react';

import SuspiciousUserCheck from 'components/Globals/SuspiciousUserCheck';

import { RENDERING_STATUSES, VISITOR_TYPES } from 'operabase-router/constants';

const noop = () => null;

const FingerprintContext = createContext({
  setTrustInfo: noop,
  visitorInfo: null,
  isTrustedBot: false,
});

const isFingerPrintEnabled = process.env.FINGER_PRINT_ENABLED === 'true';

const checkIsTrustedBot = serverTrust =>
  serverTrust?.visitorType === VISITOR_TYPES.BOT && serverTrust?.status === RENDERING_STATUSES.OVERRIDE;

const FingerprintContextProvider = ({ visitorInfo, serverTrust, renderChildrenWithMandatoryProviders, children }) => {
  const [trustInfo, setTrustInfo] = useState(serverTrust);
  const isTrustedBot = useMemo(() => checkIsTrustedBot(trustInfo), [trustInfo]);

  const fingerprintContext = useMemo(
    () => ({
      setTrustInfo,
      visitorInfo,
      isTrustedBot,
    }),
    [setTrustInfo, visitorInfo, isTrustedBot],
  );

  return (
    <FingerprintContext.Provider value={fingerprintContext}>
      {renderChildrenWithMandatoryProviders(
        <SuspiciousUserCheck visitorInfo={visitorInfo} trustInfo={trustInfo} setTrustInfo={setTrustInfo}>
          {children}
        </SuspiciousUserCheck>,
        fingerprintContext,
      )}
    </FingerprintContext.Provider>
  );
};

const VisitorDataWrapper = ({ serverTrust, children, renderChildrenWithMandatoryProviders }) => {
  const visitorInfo = useVisitorData({ extendedResult: true });

  return (
    <FingerprintContextProvider
      visitorInfo={visitorInfo}
      serverTrust={serverTrust}
      renderChildrenWithMandatoryProviders={renderChildrenWithMandatoryProviders}
    >
      {children}
    </FingerprintContextProvider>
  );
};

const FingerprintProvider = ({ renderChildrenWithMandatoryProviders, children, serverTrust }) => {
  const isTrustedBot = useMemo(() => checkIsTrustedBot(serverTrust), [serverTrust]);

  if (isTrustedBot || !isFingerPrintEnabled) {
    return (
      <FingerprintContextProvider
        serverTrust={serverTrust}
        renderChildrenWithMandatoryProviders={renderChildrenWithMandatoryProviders}
      >
        {children}
      </FingerprintContextProvider>
    );
  }

  return (
    <FpjsProvider
      loadOptions={{
        apiKey: process.env.FINGER_PRINT_SECRET_ACCESS_KEY_ID,
        ...(process.env.NODE_ENV === 'production' && { endpoint: [process.env.FINGER_PRINT_API_URL, defaultEndpoint] }),
        ...(process.env.NODE_ENV === 'production' && {
          scriptUrlPattern: [process.env.FINGER_PRINT_SCRIPT_URL, defaultScriptUrlPattern],
        }),
      }}
      cacheLocation={CacheLocation.LocalStorage}
    >
      <VisitorDataWrapper
        serverTrust={serverTrust}
        renderChildrenWithMandatoryProviders={renderChildrenWithMandatoryProviders}
      >
        {children}
      </VisitorDataWrapper>
    </FpjsProvider>
  );
};

export default FingerprintProvider;
export const useFingerprint = () => useContext(FingerprintContext);
